import { useState } from 'react';
import { toast } from 'react-toastify';
import { Metadata } from '@sodamnfoolish/sjc-backend-types/src/api/token/dto';
import * as Form from './form';
import { useBackend } from '../Hooks/UseBackend';

export function SolanaTokenParser({
    onMetadataChange,
    onError,
}: {
    onMetadataChange: (metadata: Partial<Metadata> | null) => void;
    onError?: (reason: string) => void;
}) {
    const { generateTokenMetadataByTweetUrl, generateTokenMetadataByUrl, getTokenMetadata } = useBackend();

    const [solanaTokenToParse, setSolanaTokenToParse] = useState<string>('');
    const [tweetUrlToParse, setTweetUrlToParse] = useState<string>('');
    const [webUrlToParse, setWebUrlToParse] = useState<string>('');

    async function parseTokenMetadata(mint: string) {
        try {
            const metadata = await getTokenMetadata(mint);

            if (!metadata) {
                toast.error('No metadata found');
                onMetadataChange(null);
                return;
            }

            onMetadataChange(metadata);
        } catch {
            toast.error('Error parsing token metadata');
            onError?.('Error parsing token metadata');
        }
    }

    async function generateMetadataByTweetUrl(url: string) {
        try {
            if (!url) {
                onError?.('URL not provided');
                return toast.error('URL not provided');
            }

            toast.info('Generating metadata from tweet URL...');
            const metadata = await generateTokenMetadataByTweetUrl(url);

            onMetadataChange({
                name: metadata.name,
                symbol: metadata.symbol,
                description: url,
                twitter: url,
            });

            toast.success('Metadata generated successfully!');
        } catch {
            toast.error('Error generating metadata from tweet');
            onError?.('Error generating metadata from tweet');
        }
    }

    async function generateMetadataByUrl(url: string) {
        try {
            if (!url) {
                onError?.('URL not provided');
                return toast.error('URL not provided');
            }
            toast.info('Generating metadata from web URL...');
            const metadata = await generateTokenMetadataByUrl(url);

            onMetadataChange({
                name: metadata.name,
                symbol: metadata.symbol,
                description: url,
                twitter: url,
            });

            toast.success('Metadata generated successfully!');
        } catch {
            toast.error('Error generating metadata from web URL');
            onError?.('Error generating metadata from web URL');
        }
    }

    return (
        <form className='flex flex-col gap-[16px]'>
            <fieldset className='flex flex-col gap-[7px]'>
                <div>
                    <legend className='text-[14px] font-bold'>Parse Existing Solana Token</legend>
                </div>
                <div className='flex'>
                    <Form.Input
                        className='w-full h-[34px] mr-[14px]'
                        value={solanaTokenToParse}
                        onChange={(e) => {
                            setSolanaTokenToParse(e.target.value);
                        }}
                    />
                    <Form.Button
                        className='text-[14px] font-bold'
                        onClick={() => {
                            parseTokenMetadata(solanaTokenToParse);
                        }}
                    >
                        Go
                    </Form.Button>
                </div>
            </fieldset>

            <fieldset className='flex flex-col gap-[7px]'>
                <div>
                    <legend className='text-[14px] font-bold'>Ask AI To Generate From Tweet URL</legend>
                </div>
                <div className='flex'>
                    <Form.Input
                        className='w-full h-[34px] mr-[14px]'
                        value={tweetUrlToParse}
                        onChange={(e) => {
                            setTweetUrlToParse(e.target.value);
                        }}
                    />
                    <Form.Button
                        className='text-[14px] font-bold'
                        onClick={() => {
                            generateMetadataByTweetUrl(tweetUrlToParse);
                        }}
                    >
                        Go
                    </Form.Button>
                </div>
            </fieldset>

            <fieldset className='flex flex-col gap-[7px]'>
                <div>
                    <legend className='text-[14px] font-bold'>Ask AI To Generate From Web URL</legend>
                </div>
                <div className='flex'>
                    <Form.Input
                        className='w-full h-[34px] mr-[14px]'
                        value={webUrlToParse}
                        onChange={(e) => {
                            setWebUrlToParse(e.target.value);
                        }}
                    />
                    <Form.Button
                        className='text-[14px] font-bold'
                        onClick={() => {
                            generateMetadataByUrl(webUrlToParse);
                        }}
                    >
                        Go
                    </Form.Button>
                </div>
            </fieldset>
        </form>
    );
}
